
'use client'
import { type FC, useState, useEffect, useRef, type KeyboardEvent, memo } from 'react'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { SvgChevronDown } from './SvgsInline'
import { rgbAlpha } from '@/styles'

type Props = {
  title: string
  children: React.ReactNode
  initialExpanded?: boolean
  externalForceExpanded?: boolean
  headingLevel?: 2 | 3 | 4 | 5 | 6
  onToggle?: (isExpanded: boolean) => void
}

const btnClass = 'btn--acc-trigger'
const btnSelector = `button[class~="${btnClass}"]`

export const Accordion:FC<Props> = memo(({ title, children, initialExpanded = false, externalForceExpanded, headingLevel = 3, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded)
  const accordionRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if(typeof externalForceExpanded === 'boolean') {
      queueMicrotask(() => setIsExpanded(externalForceExpanded))
      onToggle?.(externalForceExpanded)
    }
  }, [externalForceExpanded, onToggle])

  const toggleAccordion = () => {
    const newState = !isExpanded
    queueMicrotask(() => setIsExpanded(newState))
    onToggle?.(newState)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    switch (event.key) {
      case ' ':
      case 'Enter':
        event.preventDefault()
        toggleAccordion()
        break
      case 'ArrowDown':
        event.preventDefault()
        const nextButton:HTMLButtonElement = accordionRef.current?.nextElementSibling?.querySelector(btnSelector)
        nextButton?.focus()
        break
      case 'ArrowUp':
        event.preventDefault()
        const prevButton:HTMLButtonElement = accordionRef.current?.previousElementSibling?.querySelector(btnSelector)
        prevButton?.focus()
        break
      case 'Home':
        event.preventDefault()
        const firstButton:HTMLButtonElement = accordionRef.current?.parentElement?.querySelector(btnSelector)
        firstButton?.focus()
        break
      case 'End':
        event.preventDefault()
        const allButtons:NodeListOf<HTMLButtonElement> = accordionRef.current?.parentElement?.querySelectorAll(btnSelector)
        const lastButton = allButtons?.[allButtons.length - 1]
        lastButton?.focus()
        break
    }
  }

  const HeadingTag = `h${headingLevel}` as const

  return (
    <AccordionWrapper ref={accordionRef}>
      <HeadingTag role='heading' aria-level={headingLevel}>
        <AccordionButton
          ref={buttonRef}
          id={`accordion-button-${title}`}
          className={btnClass}
          onClick={toggleAccordion}
          onKeyDown={handleKeyDown}
          aria-expanded={isExpanded}
          aria-controls={`accordion-content-${title}`}
          type='button'
        >
          {title}
          <AccordionIcon $isExpanded={isExpanded} aria-hidden='true'>
            <SvgChevronDown />
          </AccordionIcon>
        </AccordionButton>
      </HeadingTag>
      <AccordionPanel
        id={`accordion-content-${title}`}
        role='region'
        aria-labelledby={`accordion-button-${title}`}
        aria-hidden={!isExpanded}
        tabIndex={isExpanded ? 0 : -1}
        initial={false}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0
        }}
        transition={{
          duration: .2,
          ease: [.4, 0, .2, 1]
        }}
      >
        <AccordionContent>
          {children}
        </AccordionContent>
      </AccordionPanel>
    </AccordionWrapper>
  )
})

const AccordionIcon = styled.span<{ $isExpanded: boolean }>`
  transform: rotate(${({ $isExpanded }) => $isExpanded ? 180 : 0}deg);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
`

const AccordionWrapper = styled.div`
  overflow: hidden;
  margin-bottom: 10px;

  h2, h3, h4, h5, h6 {
    margin: 0;
    padding: 0;
  }
`

const AccordionButton = styled.button`
  align-items: center;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  font-size: 16px;
  font-weight: bold;
  justify-content: space-between;
  min-height: 48px;
  overflow-anchor: none;
  padding: 15px;
  text-align: left;
  transition: margin 150ms cubic-bezier(.4, 0, .2, 1) 0ms, min-height 150ms cubic-bezier(.4, 0, .2, 1) 0ms, background-color 150ms cubic-bezier(.4, 0, .2, 1) 0ms, outline 150ms cubic-bezier(.4, 0, .2, 1) 0ms;
  width: 100%;

  &[aria-expanded='true'] {
    min-height: 64px;
  }

  &:hover,
  &:focus {
    background: var(--color-navbar-link-hover-bg);
    outline: 2px solid ${rgbAlpha('--color-navbar-link-hover', .7)};
    outline-offset: -5px;
  }
`

// gridTemplateRows: isExpanded ? '1fr' : '0fr',
const AccordionPanel = styled(motion.div)<{ $isExpanded?: boolean }>`
  overflow: hidden;
`

const AccordionContent = styled.div`
  overflow: hidden;
  padding: 5px 5px;
`
